import { CodeOutlined, UploadOutlined } from "@ant-design/icons";
import { Alert, Button, Divider, Input, InputNumber, Select } from "antd";
import { observer } from "mobx-react-lite";
import { useMemo, useRef, useState } from "react";
import { CDN, getENV } from "../../../../../config";
import { flattenSchema } from "../../../../../helpers/jsonSchema.helpers";
import { useMst } from "../../../../../stores/Root";
import { CRM_ADAPTERS } from "./emailThumbnail.adapters";
import {
  generateEmailThumbnail,
  GenerationResult,
  renderSampleFragment,
} from "./emailThumbnail.generator";
import {
  CrmId,
  EmailThumbnailConfig,
  HorizontalAlignment,
  SizingStrategy,
  TextLayer,
  VerticalSlot,
  createDefaultEmailThumbnailConfig,
} from "./emailThumbnail.types";
import {
  applyLongCopySamples,
  createDefaultMappings,
  createEditorRevisionState,
  getCopyReadiness,
  getUsedSchemaFields,
  markEditorDirty,
  reconcileFieldMappings,
} from "./emailThumbnail.ui";
import {
  EmailThumbnailDiagnostic,
  validateEmailThumbnailConfig,
} from "./emailThumbnail.validation";
import { EmailThumbnailOutput } from "./EmailThumbnailOutput";
import { EmailThumbnailPreview } from "./EmailThumbnailPreview";
import { EmailThumbnailSwitch } from "./EmailThumbnailSwitch";
import {
  EmailThumbnailStorage,
  uploadEmailThumbnailBackground,
} from "./emailThumbnail.upload";
import "./EmailThumbnailForm.scss";

const FIELD_NAME = /^[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*$/;
const FONT_FAMILIES = [
  "Arial, Helvetica, sans-serif",
  "Georgia, 'Times New Roman', serif",
  "'Courier New', Courier, monospace",
];
const FONT_SIZES = [
  12, 14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 48, 52,
  56, 60, 64,
];
const LINE_HEIGHTS = [
  14, 16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40, 42, 44, 48, 52, 56,
  60, 64, 68, 72,
];
const PADDINGS = [0, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40, 44, 48, 56, 64];
const PLAY_SIZES = [16, 20, 24, 28, 32, 36, 40, 44, 48, 56, 64, 72, 80];

type SelectedProject = {
  id: string;
  aliasId: string;
  thumbS3Url?: string | null;
  projectAccountDomain: string;
  stateJsonSchema: Parameters<typeof flattenSchema>[0];
  account?: { aliasId: string } | null;
};

type FieldButtonProps = {
  fields: string[];
  onInsert: (field: string) => void;
  label: string;
};

const FieldButtons = ({ fields, onInsert, label }: FieldButtonProps) => (
  <div className="email-thumbnail-field-buttons" aria-label={label}>
    <span>Insert field:</span>
    {fields.map((field) => (
      <Button key={field} size="small" onClick={() => onInsert(field)}>
        {field}
      </Button>
    ))}
  </div>
);

type TextLayerEditorProps = {
  id: string;
  title: string;
  layer: TextLayer;
  schemaFields: string[];
  onChange: (layer: TextLayer) => void;
};

const TextLayerEditor = ({
  id,
  title,
  layer,
  schemaFields,
  onChange,
}: TextLayerEditorProps) => {
  const update = <K extends keyof TextLayer>(key: K, value: TextLayer[K]) =>
    onChange({ ...layer, [key]: value });

  return (
    <fieldset className="email-thumbnail-fieldset">
      <legend>{title}</legend>
      <div className="email-thumbnail-control is-full">
        <label htmlFor={`${id}-template`}>Text template</label>
        <Input.TextArea
          id={`${id}-template`}
          value={layer.template}
          rows={2}
          onChange={(event) => update("template", event.target.value)}
          placeholder="Write text and insert named fields below"
        />
        <FieldButtons
          fields={schemaFields}
          label={`Fields available for ${title}`}
          onInsert={(field) =>
            update("template", `${layer.template}{{${field}}}`)
          }
        />
      </div>
      <div className="email-thumbnail-controls-grid">
        <div className="email-thumbnail-control is-wide">
          <label htmlFor={`${id}-font`}>Web-safe font</label>
          <Select
            id={`${id}-font`}
            value={layer.fontFamily}
            options={FONT_FAMILIES.map((value) => ({ value, label: value }))}
            onChange={(value) => update("fontFamily", value)}
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-weight`}>Weight</label>
          <Select
            id={`${id}-weight`}
            value={layer.fontWeight}
            options={[
              { value: "normal", label: "Regular" },
              { value: "bold", label: "Bold" },
            ]}
            onChange={(value) => update("fontWeight", value)}
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-size`}>Size</label>
          <Select
            id={`${id}-size`}
            value={layer.fontSize}
            options={FONT_SIZES.map((value) => ({
              value,
              label: `${value}px`,
            }))}
            onChange={(value) => update("fontSize", value)}
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-line-height`}>Line height</label>
          <Select
            id={`${id}-line-height`}
            value={layer.lineHeight}
            options={LINE_HEIGHTS.map((value) => ({
              value,
              label: `${value}px`,
            }))}
            onChange={(value) => update("lineHeight", value)}
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-color`}>Text color</label>
          <Input
            id={`${id}-color`}
            type="color"
            value={layer.color}
            onChange={(event) =>
              update("color", event.target.value.toUpperCase())
            }
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-alignment`}>Alignment</label>
          <Select
            id={`${id}-alignment`}
            value={layer.horizontalAlignment}
            options={(["left", "center", "right"] as HorizontalAlignment[]).map(
              (value) => ({
                value,
                label: value[0].toUpperCase() + value.slice(1),
              })
            )}
            onChange={(value) => update("horizontalAlignment", value)}
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-slot`}>Vertical slot</label>
          <Select
            id={`${id}-slot`}
            value={layer.verticalSlot}
            options={(["top", "center", "bottom"] as VerticalSlot[]).map(
              (value) => ({
                value,
                label: value[0].toUpperCase() + value.slice(1),
              })
            )}
            onChange={(value) => update("verticalSlot", value)}
          />
        </div>
        <div className="email-thumbnail-control">
          <label htmlFor={`${id}-padding`}>Padding</label>
          <Select
            id={`${id}-padding`}
            value={layer.padding}
            options={PADDINGS.map((value) => ({ value, label: `${value}px` }))}
            onChange={(value) => update("padding", value)}
          />
        </div>
      </div>
    </fieldset>
  );
};

const getSchemaData = (project?: SelectedProject | null) => {
  try {
    const flattened = project?.stateJsonSchema
      ? flattenSchema(project.stateJsonSchema)
      : {};
    const entries = Object.entries(flattened).filter(([field]) =>
      FIELD_NAME.test(field)
    );
    if (entries.length > 0) return Object.fromEntries(entries);
  } catch (_error) {
    // A fallback field keeps the editor usable while a project schema is unavailable.
  }
  return { blingsId: "sample-video-id" };
};

const buildDestination = (
  project: SelectedProject | null | undefined,
  field: string
) => {
  const accountAlias = project?.account?.aliasId || project?.aliasId || "video";
  const projectAlias = project?.aliasId || "project";
  const domain = project?.projectAccountDomain || "mp5.live";
  const environment = getENV();
  const path =
    environment === "master" ? projectAlias : `${environment}/${projectAlias}`;
  return `https://${accountAlias}.${domain}/${path}?${encodeURIComponent(
    field
  )}={{${field}}}`;
};

const buildInitialConfig = (
  project: SelectedProject | null | undefined,
  schemaData: Record<string, unknown>
): EmailThumbnailConfig => {
  const fields = Object.keys(schemaData);
  const personalizationField = fields[0] || "blingsId";
  const defaults = createDefaultEmailThumbnailConfig();
  defaults.backgroundUrl = project?.thumbS3Url || "";
  defaults.destinationUrl = buildDestination(project, personalizationField);
  defaults.headline.template = `A video made for {{${personalizationField}}}`;
  defaults.fieldMappings = createDefaultMappings(
    [personalizationField],
    CRM_ADAPTERS[defaults.crmId]
  );
  defaults.sampleData = Object.entries(schemaData).reduce<
    Record<string, string>
  >((samples, [field, value]) => {
    samples[field] = value == null ? `Sample ${field}` : String(value);
    return samples;
  }, {});
  return defaults;
};

const dedupeDiagnostics = (
  diagnostics: EmailThumbnailDiagnostic[]
): EmailThumbnailDiagnostic[] => {
  const seen = new Set<string>();
  return diagnostics.filter((diagnostic) => {
    const key = `${diagnostic.severity}:${diagnostic.code}:${
      diagnostic.field || ""
    }:${diagnostic.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const EmailThumbnailForm = observer(() => {
  const { project, backgroundStorage } = useMst((store) => ({
    project: store.projectsStore.selectedProject,
    backgroundStorage: store.liveControlStore,
  })) as {
    project: SelectedProject | null;
    backgroundStorage: EmailThumbnailStorage;
  };
  const schemaData = useMemo(() => getSchemaData(project), [project]);
  const schemaFields = useMemo(() => Object.keys(schemaData), [schemaData]);
  const initialConfig = useMemo(
    () => buildInitialConfig(project, schemaData),
    [project, schemaData]
  );
  const [config, setConfig] = useState<EmailThumbnailConfig>(initialConfig);
  const [editorProjectId, setEditorProjectId] = useState(project?.id);
  const [revisionState, setRevisionState] = useState(createEditorRevisionState);
  const [generatedRevision, setGeneratedRevision] = useState<number | null>(
    null
  );
  const [savedGeneration, setSavedGeneration] = useState<GenerationResult>({
    html: "",
    diagnostics: [],
  });
  const [isUploadingBackground, setIsUploadingBackground] = useState(false);
  const [backgroundUploadProgress, setBackgroundUploadProgress] = useState(0);
  const [backgroundUploadError, setBackgroundUploadError] = useState("");
  const backgroundUploadInputRef = useRef<HTMLInputElement>(null);
  const selectedProjectIdRef = useRef(project?.id);
  selectedProjectIdRef.current = project?.id;

  if (editorProjectId !== project?.id) {
    setEditorProjectId(project?.id);
    setConfig(initialConfig);
    setRevisionState(createEditorRevisionState());
    setGeneratedRevision(null);
    setSavedGeneration({ html: "", diagnostics: [] });
    setIsUploadingBackground(false);
    setBackgroundUploadProgress(0);
    setBackgroundUploadError("");
  }

  const commitConfig = (
    update: (current: EmailThumbnailConfig) => EmailThumbnailConfig
  ) => {
    setConfig((current) => {
      const next = update(current);
      return {
        ...next,
        fieldMappings: reconcileFieldMappings(
          next,
          schemaFields,
          CRM_ADAPTERS[next.crmId]
        ),
      };
    });
    setRevisionState((current) => markEditorDirty(current));
  };

  const usedFields = useMemo(
    () => getUsedSchemaFields(config, schemaFields),
    [config, schemaFields]
  );
  const generationContext = useMemo(() => ({ schemaFields }), [schemaFields]);
  const draftGeneration = useMemo(
    () => generateEmailThumbnail(config, generationContext),
    [config, generationContext]
  );
  const diagnostics = useMemo(
    () =>
      dedupeDiagnostics([
        ...validateEmailThumbnailConfig(config, schemaFields),
        ...draftGeneration.diagnostics,
      ]),
    [config, draftGeneration.diagnostics, schemaFields]
  );
  const hasGenerated = generatedRevision !== null;
  const isDirty = !hasGenerated || revisionState.revision !== generatedRevision;
  const readiness = getCopyReadiness({
    diagnostics,
    dirty: isDirty,
    saved: hasGenerated,
  });
  const previewFragment = useMemo(
    () => renderSampleFragment(draftGeneration.html, config.sampleData, config),
    [config, draftGeneration.html]
  );

  const generate = () => {
    const configSnapshot = JSON.parse(
      JSON.stringify(config)
    ) as EmailThumbnailConfig;
    setSavedGeneration(
      generateEmailThumbnail(configSnapshot, generationContext)
    );
    setGeneratedRevision(revisionState.revision);
  };

  const uploadBackground = async (file: File) => {
    if (!project || isUploadingBackground) return;
    const uploadProjectId = project.id;
    setIsUploadingBackground(true);
    setBackgroundUploadProgress(0);
    setBackgroundUploadError("");
    try {
      const backgroundUrl = await uploadEmailThumbnailBackground({
        cdnBaseUrl: CDN[getENV()],
        file,
        projectId: uploadProjectId,
        storage: backgroundStorage,
        onProgress: ({ loaded, total }) =>
          setBackgroundUploadProgress(
            total ? Math.min(100, Math.round((loaded / total) * 100)) : 0
          ),
      });
      if (selectedProjectIdRef.current !== uploadProjectId) return;
      commitConfig((current) => ({ ...current, backgroundUrl }));
      setBackgroundUploadProgress(100);
    } catch (caught) {
      if (selectedProjectIdRef.current !== uploadProjectId) return;
      setBackgroundUploadError(
        caught instanceof Error
          ? caught.message
          : "Could not upload the background image."
      );
    } finally {
      if (selectedProjectIdRef.current === uploadProjectId) {
        setIsUploadingBackground(false);
      }
    }
  };

  const statusMessage = !hasGenerated
    ? revisionState.revision === 0
      ? "Not generated"
      : "Changes not generated"
    : isDirty
    ? "Changes not generated"
    : "Generated";

  const selectCrm = (crmId: CrmId) => {
    const adapter = CRM_ADAPTERS[crmId];
    commitConfig((current) => ({
      ...current,
      crmId,
      sizingStrategy:
        (current.sizingStrategy === "native-length" &&
          !adapter.capabilities.nativeLength) ||
        (current.sizingStrategy === "precomputed-bucket" &&
          !adapter.capabilities.precomputedBucket)
          ? "fixed"
          : current.sizingStrategy,
      fieldMappings: createDefaultMappings(
        Array.from(
          new Set([...Object.keys(current.fieldMappings), ...usedFields])
        ),
        adapter
      ),
    }));
  };

  if (!project) {
    return (
      <Alert
        type="warning"
        showIcon
        message="Select a project to configure an email thumbnail."
      />
    );
  }

  const adapter = CRM_ADAPTERS[config.crmId];
  const sizingOptions: Array<{ value: SizingStrategy; label: string }> = [
    { value: "fixed", label: "Fixed and wrapping (all email clients)" },
  ];
  if (adapter.capabilities.nativeLength) {
    sizingOptions.push({
      value: "native-length",
      label: "CRM-native length rules",
    });
  }
  if (adapter.capabilities.precomputedBucket) {
    sizingOptions.push({
      value: "precomputed-bucket",
      label: "Precomputed bucket field",
    });
  }

  return (
    <div className="email-thumbnail-editor">
      <header className="email-thumbnail-header">
        <div>
          <span className="email-thumbnail-eyebrow">Email Thumbnail</span>
          <h1>Build a personalized email-safe thumbnail</h1>
          <p>
            Configure live text over a static hosted image, then generate and
            copy one self-contained table fragment into your CRM.
          </p>
        </div>
        <div className="email-thumbnail-save-area">
          <span
            className={`email-thumbnail-save-status is-${
              isDirty ? "dirty" : "saved"
            }`}
            aria-live="polite"
            role="status"
          >
            {statusMessage}
          </span>
          <Button
            type="primary"
            icon={<CodeOutlined />}
            disabled={!isDirty}
            onClick={generate}
          >
            Generate fragment
          </Button>
        </div>
      </header>

      <div className="email-thumbnail-workspace">
        <main className="email-thumbnail-design-panel">
          <section
            className="email-thumbnail-card"
            aria-labelledby="thumbnail-foundation-title"
          >
            <div className="email-thumbnail-section-heading">
              <div>
                <h2 id="thumbnail-foundation-title">
                  Canvas &amp; destination
                </h2>
                <p>
                  Use a public HTTPS background and a personalized Blings
                  landing URL.
                </p>
              </div>
            </div>
            <div className="email-thumbnail-controls-grid">
              <div className="email-thumbnail-control is-full">
                <label htmlFor="email-thumbnail-background">
                  Background image URL
                </label>
                <div className="email-thumbnail-background-row">
                  <Input
                    id="email-thumbnail-background"
                    value={config.backgroundUrl}
                    placeholder="https://storage.example.com/thumbnail.jpg"
                    onChange={(event) =>
                      commitConfig((current) => ({
                        ...current,
                        backgroundUrl: event.target.value,
                      }))
                    }
                  />
                  <input
                    id="email-thumbnail-background-upload"
                    ref={backgroundUploadInputRef}
                    className="email-thumbnail-upload-input"
                    type="file"
                    accept="image/jpeg,image/png"
                    aria-label="Upload background image"
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      event.currentTarget.value = "";
                      if (file) void uploadBackground(file);
                    }}
                  />
                  <Button
                    icon={<UploadOutlined />}
                    loading={isUploadingBackground}
                    disabled={isUploadingBackground}
                    onClick={() => backgroundUploadInputRef.current?.click()}
                  >
                    Upload image
                  </Button>
                </div>
                <div className="email-thumbnail-upload-feedback">
                  <span className="email-thumbnail-help" aria-live="polite">
                    {isUploadingBackground
                      ? `Uploading image… ${backgroundUploadProgress}%`
                      : "JPG or PNG. The uploaded image is hosted on the Blings CDN."}
                  </span>
                  {backgroundUploadError && (
                    <span className="email-thumbnail-upload-error" role="alert">
                      {backgroundUploadError}
                    </span>
                  )}
                </div>
                <span className="email-thumbnail-help">
                  Defaults to the project's published thumbnail.
                </span>
              </div>
              <div className="email-thumbnail-control is-full">
                <label htmlFor="email-thumbnail-destination">
                  Destination URL
                </label>
                <Input
                  id="email-thumbnail-destination"
                  value={config.destinationUrl}
                  onChange={(event) =>
                    commitConfig((current) => ({
                      ...current,
                      destinationUrl: event.target.value,
                    }))
                  }
                />
                <FieldButtons
                  fields={schemaFields}
                  label="Fields available for the destination URL"
                  onInsert={(field) =>
                    commitConfig((current) => ({
                      ...current,
                      destinationUrl: `${current.destinationUrl}{{${field}}}`,
                    }))
                  }
                />
              </div>
              <div className="email-thumbnail-control">
                <label htmlFor="email-thumbnail-width">Width (px)</label>
                <InputNumber
                  id="email-thumbnail-width"
                  min={1}
                  max={1200}
                  precision={0}
                  value={config.width}
                  onChange={(value) =>
                    typeof value === "number" &&
                    commitConfig((current) => ({ ...current, width: value }))
                  }
                />
              </div>
              <div className="email-thumbnail-control">
                <label htmlFor="email-thumbnail-height">Height (px)</label>
                <InputNumber
                  id="email-thumbnail-height"
                  min={1}
                  max={1200}
                  precision={0}
                  value={config.height}
                  onChange={(value) =>
                    typeof value === "number" &&
                    commitConfig((current) => ({ ...current, height: value }))
                  }
                />
              </div>
              <div className="email-thumbnail-control">
                <label htmlFor="email-thumbnail-fallback-color">
                  Fallback color
                </label>
                <Input
                  id="email-thumbnail-fallback-color"
                  type="color"
                  value={config.fallbackColor}
                  onChange={(event) =>
                    commitConfig((current) => ({
                      ...current,
                      fallbackColor: event.target.value.toUpperCase(),
                    }))
                  }
                />
              </div>
              <div className="email-thumbnail-control is-wide">
                <label htmlFor="email-thumbnail-fallback-text">
                  Accessible fallback text
                </label>
                <Input
                  id="email-thumbnail-fallback-text"
                  value={config.fallbackText}
                  onChange={(event) =>
                    commitConfig((current) => ({
                      ...current,
                      fallbackText: event.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </section>

          <section
            className="email-thumbnail-card"
            aria-labelledby="thumbnail-copy-title"
          >
            <div className="email-thumbnail-section-heading">
              <div>
                <h2 id="thumbnail-copy-title">Live copy</h2>
                <p>
                  Personalization stays as real text, never flattened into the
                  image.
                </p>
              </div>
            </div>
            <TextLayerEditor
              id="email-thumbnail-headline"
              title="Headline"
              layer={config.headline}
              schemaFields={schemaFields}
              onChange={(headline) =>
                commitConfig((current) => ({ ...current, headline }))
              }
            />
            <div className="email-thumbnail-toggle-row">
              <div>
                <label htmlFor="email-thumbnail-supporting-toggle">
                  Supporting line
                </label>
                <span>
                  Add a second line with independent type and placement.
                </span>
              </div>
              <EmailThumbnailSwitch
                id="email-thumbnail-supporting-toggle"
                label="Show supporting line"
                checked={Boolean(config.supportingLine)}
                onChange={(checked) =>
                  commitConfig((current) => ({
                    ...current,
                    supportingLine: checked
                      ? {
                          ...current.headline,
                          template: "",
                          fontWeight: "normal",
                          fontSize: 18,
                          lineHeight: 24,
                          verticalSlot: "bottom",
                        }
                      : null,
                  }))
                }
              />
            </div>
            {config.supportingLine && (
              <TextLayerEditor
                id="email-thumbnail-supporting"
                title="Supporting line"
                layer={config.supportingLine}
                schemaFields={schemaFields}
                onChange={(supportingLine) =>
                  commitConfig((current) => ({ ...current, supportingLine }))
                }
              />
            )}
          </section>

          <section
            className="email-thumbnail-card"
            aria-labelledby="thumbnail-play-title"
          >
            <div className="email-thumbnail-section-heading">
              <div>
                <h2 id="thumbnail-play-title">Play indicator</h2>
                <p>An email-safe inline play mark, placed with the headline.</p>
              </div>
              <EmailThumbnailSwitch
                id="email-thumbnail-play-toggle"
                label="Show play indicator"
                checked={config.playIndicator.enabled}
                onChange={(enabled) =>
                  commitConfig((current) => ({
                    ...current,
                    playIndicator: { ...current.playIndicator, enabled },
                  }))
                }
              />
            </div>
            <div className="email-thumbnail-controls-grid">
              <div className="email-thumbnail-control">
                <label htmlFor="email-thumbnail-play-size">Size</label>
                <Select
                  id="email-thumbnail-play-size"
                  value={config.playIndicator.size}
                  disabled={!config.playIndicator.enabled}
                  options={PLAY_SIZES.map((value) => ({
                    value,
                    label: `${value}px`,
                  }))}
                  onChange={(size) =>
                    commitConfig((current) => ({
                      ...current,
                      playIndicator: { ...current.playIndicator, size },
                    }))
                  }
                />
              </div>
              <div className="email-thumbnail-control">
                <label htmlFor="email-thumbnail-play-foreground">
                  Icon color
                </label>
                <Input
                  id="email-thumbnail-play-foreground"
                  type="color"
                  value={config.playIndicator.foregroundColor}
                  disabled={!config.playIndicator.enabled}
                  onChange={(event) =>
                    commitConfig((current) => ({
                      ...current,
                      playIndicator: {
                        ...current.playIndicator,
                        foregroundColor: event.target.value.toUpperCase(),
                      },
                    }))
                  }
                />
              </div>
              <div className="email-thumbnail-control">
                <label htmlFor="email-thumbnail-play-background">
                  Button color
                </label>
                <Input
                  id="email-thumbnail-play-background"
                  type="color"
                  value={config.playIndicator.backgroundColor}
                  disabled={!config.playIndicator.enabled}
                  onChange={(event) =>
                    commitConfig((current) => ({
                      ...current,
                      playIndicator: {
                        ...current.playIndicator,
                        backgroundColor: event.target.value.toUpperCase(),
                      },
                    }))
                  }
                />
              </div>
            </div>
          </section>

          <section
            className="email-thumbnail-card"
            aria-labelledby="thumbnail-crm-title"
          >
            <div className="email-thumbnail-section-heading">
              <div>
                <h2 id="thumbnail-crm-title">CRM personalization</h2>
                <p>
                  Map named project fields to the exact merge tokens your CRM
                  expects.
                </p>
              </div>
            </div>
            <div className="email-thumbnail-controls-grid">
              <div className="email-thumbnail-control is-wide">
                <label htmlFor="email-thumbnail-crm">CRM</label>
                <Select
                  id="email-thumbnail-crm"
                  value={config.crmId}
                  options={Object.values(CRM_ADAPTERS).map((crm) => ({
                    value: crm.id,
                    label: crm.label,
                  }))}
                  onChange={selectCrm}
                />
              </div>
              <div className="email-thumbnail-control is-wide">
                <label htmlFor="email-thumbnail-sizing">
                  Text sizing strategy
                </label>
                <Select
                  id="email-thumbnail-sizing"
                  value={config.sizingStrategy}
                  options={sizingOptions}
                  onChange={(sizingStrategy) =>
                    commitConfig((current) => ({
                      ...current,
                      sizingStrategy,
                      bucketField:
                        sizingStrategy === "precomputed-bucket"
                          ? current.bucketField || schemaFields[0]
                          : current.bucketField,
                    }))
                  }
                />
              </div>
              {config.sizingStrategy === "fixed" && (
                <div className="email-thumbnail-control">
                  <label htmlFor="email-thumbnail-max-lines">
                    Headline max lines
                  </label>
                  <InputNumber
                    id="email-thumbnail-max-lines"
                    min={1}
                    max={6}
                    precision={0}
                    value={config.maxLines}
                    onChange={(maxLines) =>
                      typeof maxLines === "number" &&
                      commitConfig((current) => ({ ...current, maxLines }))
                    }
                  />
                  <span className="email-thumbnail-help">
                    Warning budget only; email-safe source does not clip live
                    text.
                  </span>
                </div>
              )}
            </div>
            <p className="email-thumbnail-compatibility">
              Fixed sizing is available for every CRM. CRM-native length rules
              are available only for Salesforce Marketing Cloud; precomputed
              buckets are available only for Mailchimp.
            </p>

            {config.sizingStrategy === "precomputed-bucket" && (
              <div className="email-thumbnail-control is-full">
                <label htmlFor="email-thumbnail-bucket-field">
                  Bucket field
                </label>
                <Select
                  id="email-thumbnail-bucket-field"
                  value={config.bucketField || undefined}
                  options={schemaFields.map((field) => ({
                    value: field,
                    label: field,
                  }))}
                  onChange={(bucketField) =>
                    commitConfig((current) => ({ ...current, bucketField }))
                  }
                />
                <span className="email-thumbnail-help">
                  Your CRM should populate this field with large, medium, or
                  small.
                </span>
              </div>
            )}

            {config.sizingStrategy !== "fixed" && (
              <fieldset className="email-thumbnail-fieldset email-thumbnail-buckets">
                <legend>Adaptive sizing buckets</legend>
                {config.sizingBuckets.map((bucket, index) => (
                  <div className="email-thumbnail-bucket-row" key={index}>
                    <strong>{["Large", "Medium", "Small"][index]}</strong>
                    <div className="email-thumbnail-control">
                      <label htmlFor={`email-thumbnail-bucket-${index}-max`}>
                        Max characters
                      </label>
                      <InputNumber
                        id={`email-thumbnail-bucket-${index}-max`}
                        min={1}
                        max={120}
                        precision={0}
                        value={bucket.maxCharacters}
                        onChange={(value) => {
                          if (typeof value !== "number") return;
                          commitConfig((current) => ({
                            ...current,
                            sizingBuckets: current.sizingBuckets.map(
                              (item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, maxCharacters: value }
                                  : item
                            ),
                          }));
                        }}
                      />
                    </div>
                    <div className="email-thumbnail-control">
                      <label htmlFor={`email-thumbnail-bucket-${index}-size`}>
                        Font size
                      </label>
                      <Select
                        id={`email-thumbnail-bucket-${index}-size`}
                        value={bucket.fontSize}
                        options={FONT_SIZES.map((value) => ({
                          value,
                          label: `${value}px`,
                        }))}
                        onChange={(fontSize) =>
                          commitConfig((current) => ({
                            ...current,
                            sizingBuckets: current.sizingBuckets.map(
                              (item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, fontSize }
                                  : item
                            ),
                          }))
                        }
                      />
                    </div>
                    <div className="email-thumbnail-control">
                      <label htmlFor={`email-thumbnail-bucket-${index}-line`}>
                        Line height
                      </label>
                      <Select
                        id={`email-thumbnail-bucket-${index}-line`}
                        value={bucket.lineHeight}
                        options={LINE_HEIGHTS.map((value) => ({
                          value,
                          label: `${value}px`,
                        }))}
                        onChange={(lineHeight) =>
                          commitConfig((current) => ({
                            ...current,
                            sizingBuckets: current.sizingBuckets.map(
                              (item, itemIndex) =>
                                itemIndex === index
                                  ? { ...item, lineHeight }
                                  : item
                            ),
                          }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </fieldset>
            )}

            <Divider />
            <div className="email-thumbnail-mappings">
              <h3>Named field mappings</h3>
              {usedFields.length === 0 ? (
                <p>
                  Insert a project field into copy or destination to create a
                  mapping.
                </p>
              ) : (
                usedFields.map((field) => (
                  <div className="email-thumbnail-mapping-row" key={field}>
                    <label htmlFor={`email-thumbnail-mapping-${field}`}>
                      {field}
                    </label>
                    <Input
                      id={`email-thumbnail-mapping-${field}`}
                      value={config.fieldMappings[field] || ""}
                      onChange={(event) =>
                        commitConfig((current) => ({
                          ...current,
                          fieldMappings: {
                            ...current.fieldMappings,
                            [field]: event.target.value,
                          },
                        }))
                      }
                    />
                  </div>
                ))
              )}
            </div>
          </section>

          <section
            className="email-thumbnail-card"
            aria-labelledby="thumbnail-samples-title"
          >
            <div className="email-thumbnail-section-heading">
              <div>
                <h2 id="thumbnail-samples-title">Sample values</h2>
                <p>
                  Preview data never changes the CRM tokens in copied source.
                </p>
              </div>
              <Button
                onClick={() =>
                  commitConfig((current) => ({
                    ...current,
                    sampleData: applyLongCopySamples(
                      usedFields,
                      current.sampleData
                    ),
                  }))
                }
              >
                Use long-copy preset
              </Button>
            </div>
            <div className="email-thumbnail-samples-grid">
              {usedFields.map((field) => (
                <div className="email-thumbnail-control" key={field}>
                  <label htmlFor={`email-thumbnail-sample-${field}`}>
                    {field}
                  </label>
                  <Input
                    id={`email-thumbnail-sample-${field}`}
                    value={config.sampleData[field] || ""}
                    onChange={(event) =>
                      commitConfig((current) => ({
                        ...current,
                        sampleData: {
                          ...current.sampleData,
                          [field]: event.target.value,
                        },
                      }))
                    }
                  />
                </div>
              ))}
            </div>
          </section>
        </main>

        <aside className="email-thumbnail-preview-panel">
          <div className="email-thumbnail-preview-sticky">
            <div className="email-thumbnail-card">
              <EmailThumbnailPreview
                fragment={previewFragment}
                width={config.width}
                height={config.height}
              />
            </div>
            <div className="email-thumbnail-card">
              <EmailThumbnailOutput
                fragment={savedGeneration.html}
                diagnostics={diagnostics}
                readiness={readiness}
                projectId={project.id}
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
});
